import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft,
  Home,
  Phone,
  MapPin,
  DollarSign,
  Bed,
  Bath,
  Square,
  Upload,
  FileText,
  MessageSquare,
  Send,
  Download,
  Image,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  HandCoins,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_deal-dashboard-16/artifacts/mtal5hnl_0e8de284-294a-4a25-8f18-7d7cab9aad20.png";

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.role === "admin";

  const [property, setProperty] = useState(null);
  const [messages, setMessages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [users, setUsers] = useState([]);

  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const [showContractModal, setShowContractModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [contractClientId, setContractClientId] = useState("");
  const [offerAmount, setOfferAmount] = useState("");
  const [offerNotes, setOfferNotes] = useState("");

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const titleWorkInputRef = useRef(null);
  const poInputRef = useRef(null);

  useEffect(() => {
    fetchProperty();
    fetchMessages();
    fetchOffers();

    if (isAdmin) {
      fetchUsers();
    }

    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (property?.images?.length > 0) {
      setSelectedImage((current) => {
        if (
          current &&
          property.images.some((img) => img.storage_path === current.storage_path)
        ) {
          return current;
        }
        return property.images[0];
      });
    } else {
      setSelectedImage(null);
    }
  }, [property]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`, {
        headers: getAuthHeaders(),
      });
      setProperty(response.data);
    } catch (error) {
      toast.error("Failed to load property");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${id}`, {
        headers: getAuthHeaders(),
      });
      setMessages(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers?property_id=${id}`, {
        headers: getAuthHeaders(),
      });
      setOffers(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data.filter((u) => u.role === "client"));
    } catch (error) {
      // Silently fail
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const submitOffer = async () => {
    if (!offerAmount) {
      toast.error("Please enter an offer amount");
      return;
    }

    try {
      await axios.post(
        `${API}/offers`,
        {
          property_id: id,
          amount: parseFloat(offerAmount),
          notes: offerNotes,
        },
        { headers: getAuthHeaders() }
      );

      toast.success("Offer submitted!");
      setShowOfferModal(false);
      setOfferAmount("");
      setOfferNotes("");
      fetchOffers();
    } catch (error) {
      toast.error("Failed to submit offer");
    }
  };

  const acceptCounterOffer = async (offerId) => {
    try {
      await axios.post(
        `${API}/offers/${offerId}/accept-counter`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Counter offer accepted!");
      fetchOffers();
      fetchProperty();
    } catch (error) {
      toast.error("Failed to accept counter offer");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(
        `${API}/messages`,
        {
          property_id: id,
          content: newMessage,
        },
        { headers: getAuthHeaders() }
      );
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/properties/${id}/images`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Image uploaded!");
      fetchProperty();
    } catch (error) {
      toast.error("Failed to upload image");
    }
  };

  const handleTitleWorkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/properties/${id}/title-work`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Title work uploaded!");
      fetchProperty();
    } catch (error) {
      toast.error("Failed to upload title work");
    }
  };

  const handlePOUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/properties/${id}/po`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("PO uploaded!");
      fetchProperty();
    } catch (error) {
      toast.error("Failed to upload PO");
    }
  };

  const handleContractUpdate = async () => {
    try {
      await axios.post(
        `${API}/properties/${id}/contract`,
        {
          under_contract: true,
          contract_client_id: contractClientId,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Contract status updated!");
      setShowContractModal(false);
      fetchProperty();
    } catch (error) {
      toast.error("Failed to update contract");
    }
  };

  const handleRemoveContract = async () => {
    try {
      await axios.post(
        `${API}/properties/${id}/contract`,
        {
          under_contract: false,
          contract_client_id: null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Contract removed!");
      fetchProperty();
    } catch (error) {
      toast.error("Failed to remove contract");
    }
  };

  const handleVisibilityUpdate = async (phoneVisible, titleVisible) => {
    if (!selectedClient) return;

    try {
      await axios.post(
        `${API}/properties/${id}/visibility/${selectedClient.id}`,
        {
          phone_visible: phoneVisible,
          title_work_visible: titleVisible,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Visibility updated!");
      setShowVisibilityModal(false);
      fetchProperty();
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const downloadFile = async (path, filename) => {
    try {
      const response = await axios.get(`${API}/files/${path}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-orange border-t-transparent"></div>
      </div>
    );
  }

  if (!property) return null;

  const phoneNumber = property.phone_masked
    ? "XXX-XXX-XXXX"
    : "1 (888) 871-4928";

  const assignedClients = users.filter((u) =>
    property.assigned_clients?.includes(u.id)
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="property-detail">
      <header className="bg-brand-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-white hover:bg-white/10"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <img
                src={LOGO_URL}
                alt="Mitten Deals"
                className="h-8 w-auto hidden sm:block"
              />
            </div>

            <div
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                property.under_contract ? "bg-yellow-500" : "bg-green-500"
              }`}
            >
              {property.under_contract ? "Under Contract" : "Available"}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-surface overflow-hidden">
              <div className="aspect-video bg-slate-200 relative">
                {selectedImage ? (
                  <img
                    src={`${API}/files/${selectedImage.storage_path}?auth=${localStorage.getItem("token")}`}
                    alt={property.address}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => setShowImageModal(true)}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <Home className="w-20 h-20 mb-4" />
                    <p>No images available</p>
                  </div>
                )}
              </div>

              {property.images?.length > 0 && (
                <div className="p-4 grid grid-cols-4 gap-2">
                  {property.images.map((img, i) => {
                    const isActive =
                      selectedImage?.storage_path === img.storage_path;

                    return (
                      <button
                        key={img.id || i}
                        type="button"
                        onClick={() => setSelectedImage(img)}
                        className={`aspect-square bg-slate-200 rounded-lg overflow-hidden border-2 transition ${
                          isActive
                            ? "border-orange-500"
                            : "border-transparent hover:border-orange-400"
                        }`}
                      >
                        <img
                          src={`${API}/files/${img.storage_path}?auth=${localStorage.getItem("token")}`}
                          alt={`View ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {isAdmin && (
                <div className="p-4 border-t">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full"
                    data-testid="upload-image-button"
                  >
                    <Image className="w-4 h-4 mr-2" /> Upload Image
                  </Button>
                </div>
              )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">
                  Details
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex-1">
                  Documents
                </TabsTrigger>
                <TabsTrigger value="messages" className="flex-1">
                  Messages
                </TabsTrigger>
                {isAdmin && (
                  <TabsTrigger value="admin" className="flex-1">
                    Admin
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="card-surface p-6 mt-4">
                <h2 className="text-2xl font-bold text-brand-primary mb-2">
                  {property.address}
                </h2>

                <div className="flex items-center gap-2 text-slate-500 mb-6">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {property.city}, {property.state} {property.zip_code}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <DollarSign className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-brand-primary">
                      ${property.price?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Price</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <Bed className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-brand-primary">
                      {property.bedrooms}
                    </p>
                    <p className="text-xs text-slate-500">Bedrooms</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <Bath className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-brand-primary">
                      {property.bathrooms}
                    </p>
                    <p className="text-xs text-slate-500">Bathrooms</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <Square className="w-6 h-6 text-brand-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-brand-primary">
                      {property.sqft?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Sq Ft</p>
                  </div>
                </div>

                {property.description && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-brand-primary mb-2">
                      Description
                    </h3>
                    <p className="text-slate-600">{property.description}</p>
                  </div>
                )}

                <div className="border-t pt-6">
                  <h3 className="font-semibold text-brand-primary mb-4">
                    Contact
                  </h3>

                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                      <Phone className="w-6 h-6 text-brand-orange" />
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">Phone Number</p>
                      <p
                        className="font-semibold text-brand-primary text-lg"
                        data-testid="property-phone"
                      >
                        {phoneNumber}
                      </p>
                      {property.phone_masked && (
                        <p className="text-xs text-slate-400">
                          Contact admin to reveal
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documents" className="card-surface p-6 mt-4">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-brand-primary">
                      Title Work
                    </h3>
                    {isAdmin && (
                      <>
                        <input
                          type="file"
                          ref={titleWorkInputRef}
                          onChange={handleTitleWorkUpload}
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => titleWorkInputRef.current?.click()}
                          data-testid="upload-title-work-button"
                        >
                          <Upload className="w-4 h-4 mr-2" /> Upload
                        </Button>
                      </>
                    )}
                  </div>

                  {property.title_work_files?.length > 0 ? (
                    <div className="space-y-2">
                      {property.title_work_files.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-brand-orange" />
                            <span className="text-sm">
                              {doc.original_filename}
                            </span>
                          </div>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              downloadFile(
                                doc.storage_path,
                                doc.original_filename
                              )
                            }
                            data-testid={`download-title-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">
                      No title work documents available.
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-brand-primary">
                      Purchase Orders
                    </h3>

                    <input
                      type="file"
                      ref={poInputRef}
                      onChange={handlePOUpload}
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      className="hidden"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => poInputRef.current?.click()}
                      data-testid="upload-po-button"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Upload PO
                    </Button>
                  </div>

                  {property.po_files?.length > 0 ? (
                    <div className="space-y-2">
                      {property.po_files.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-green-600" />
                            <div>
                              <span className="text-sm block">
                                {doc.original_filename}
                              </span>
                              <span className="text-xs text-slate-400">
                                Uploaded by {doc.uploaded_by_name}
                              </span>
                            </div>
                          </div>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              downloadFile(
                                doc.storage_path,
                                doc.original_filename
                              )
                            }
                            data-testid={`download-po-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">
                      No purchase orders uploaded.
                    </p>
                  )}
                </div>
              </TabsContent>

              <TabsContent
                value="messages"
                className="card-surface mt-4 flex flex-col"
                style={{ height: "500px" }}
              >
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-brand-primary">Messages</h3>
                  <p className="text-xs text-slate-500">
                    Communicate with {isAdmin ? "the client" : "the seller"}{" "}
                    about this property
                  </p>
                </div>

                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <MessageSquare className="w-12 h-12 mx-auto mb-4" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            msg.sender_role === "admin"
                              ? "justify-start"
                              : "justify-end"
                          }`}
                        >
                          <div
                            className={`max-w-[70%] ${
                              msg.sender_role === "admin"
                                ? "chat-admin"
                                : "chat-client"
                            } p-3`}
                          >
                            <p className="text-xs font-semibold mb-1 opacity-70">
                              {msg.sender_name}
                            </p>
                            <p className="text-sm">{msg.content}</p>
                            <p className="text-xs opacity-50 mt-1">
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="input-field"
                      onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      data-testid="message-input"
                    />
                    <Button
                      onClick={sendMessage}
                      className="btn-primary"
                      data-testid="send-message-button"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {isAdmin && (
                <TabsContent value="admin" className="card-surface p-6 mt-4">
                  <div className="mb-8">
                    <h3 className="font-semibold text-brand-primary mb-4">
                      Contract Status
                    </h3>

                    {property.under_contract ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                          <span className="font-medium text-yellow-800">
                            Under Contract
                          </span>
                        </div>

                        <p className="text-sm text-yellow-700 mb-3">
                          Contract holder:{" "}
                          {users.find(
                            (u) => u.id === property.contract_client_id
                          )?.name || "Unknown"}
                        </p>

                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveContract}
                          data-testid="remove-contract-button"
                        >
                          Remove Contract
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowContractModal(true)}
                        className="btn-primary"
                        data-testid="mark-contract-button"
                      >
                        Mark Under Contract
                      </Button>
                    )}
                  </div>

                  <div className="mb-8">
                    <h3 className="font-semibold text-brand-primary mb-4">
                      Assigned Clients
                    </h3>

                    {assignedClients.length > 0 ? (
                      <div className="space-y-2">
                        {assignedClients.map((client) => (
                          <div
                            key={client.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                {client.name.charAt(0)}
                              </div>

                              <div>
                                <p className="font-medium text-sm">
                                  {client.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {client.email}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {property.phone_visible_to?.includes(client.id) && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                  Phone
                                </span>
                              )}

                              {property.title_work_visible_to?.includes(
                                client.id
                              ) && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                  Title
                                </span>
                              )}

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setShowVisibilityModal(true);
                                }}
                                data-testid={`edit-visibility-${client.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">
                        No clients assigned to this property.
                      </p>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold text-brand-primary mb-4">
                      Status Timeline
                    </h3>

                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            Property Listed
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(property.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {property.po_files?.length > 0 && (
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">PO Received</p>
                            <p className="text-xs text-slate-500">
                              {new Date(
                                property.po_files[0].uploaded_at
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )}

                      {property.title_work_files?.length > 0 && (
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              Title Work Added
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(
                                property.title_work_files[0].uploaded_at
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )}

                      {property.under_contract ? (
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Clock className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              Under Contract
                            </p>
                            <p className="text-xs text-slate-500">
                              Awaiting closing
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                            <Clock className="w-4 h-4 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-400">
                              Under Contract
                            </p>
                            <p className="text-xs text-slate-400">Pending</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>

          <div className="space-y-6">
            <div className="card-surface p-6">
              <h3 className="font-semibold text-brand-primary mb-4">
                Quick Actions
              </h3>

              <div className="space-y-3">
                {!isAdmin && !property.under_contract && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => setShowOfferModal(true)}
                    data-testid="make-offer-button"
                  >
                    <HandCoins className="w-4 h-4 mr-2" /> Make an Offer
                  </Button>
                )}

                <input
                  type="file"
                  ref={poInputRef}
                  onChange={handlePOUpload}
                  accept=".pdf,.doc,.docx,.jpg,.png"
                  className="hidden"
                />
                <Button
                  className="w-full btn-primary"
                  onClick={() => poInputRef.current?.click()}
                  data-testid="sidebar-upload-po"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload PO
                </Button>

                <Button
                  className="w-full btn-secondary"
                  onClick={() => setActiveTab("messages")}
                  data-testid="sidebar-send-message"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Send Message
                </Button>
              </div>
            </div>

            {!isAdmin && offers.length > 0 && (
              <div className="card-surface p-6">
                <h3 className="font-semibold text-brand-primary mb-4">
                  My Offers
                </h3>

                <div className="space-y-3">
                  {offers.map((offer) => (
                    <div key={offer.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-bold text-brand-orange">
                          ${offer.amount?.toLocaleString()}
                        </p>

                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            offer.status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : offer.status === "accepted"
                                ? "bg-green-100 text-green-700"
                                : offer.status === "counter"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-red-100 text-red-700"
                          }`}
                        >
                          {offer.status}
                        </span>
                      </div>

                      {offer.status === "counter" && (
                        <div className="mt-2 p-2 bg-blue-50 rounded">
                          <p className="text-sm text-blue-700">
                            Counter: ${offer.counter_amount?.toLocaleString()}
                          </p>
                          {offer.admin_notes && (
                            <p className="text-xs text-blue-600 mt-1">
                              {offer.admin_notes}
                            </p>
                          )}
                          <Button
                            size="sm"
                            className="mt-2 bg-green-600 hover:bg-green-700 w-full"
                            onClick={() => acceptCounterOffer(offer.id)}
                            data-testid={`accept-counter-${offer.id}`}
                          >
                            Accept Counter Offer
                          </Button>
                        </div>
                      )}

                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(offer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card-surface p-6">
              <h3 className="font-semibold text-brand-primary mb-4">
                Contact Seller
              </h3>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <p className="font-semibold" data-testid="sidebar-phone">
                    {phoneNumber}
                  </p>
                  {property.phone_masked && (
                    <p className="text-xs text-slate-400">
                      Contact admin to reveal
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="card-surface p-6">
              <h3 className="font-semibold text-brand-primary mb-4">
                Property Info
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span
                    className={`font-semibold ${
                      property.under_contract
                        ? "text-yellow-600"
                        : "text-green-600"
                    }`}
                  >
                    {property.under_contract ? "Under Contract" : "Available"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500">Images</span>
                  <span className="font-semibold">
                    {property.images?.length || 0}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500">Documents</span>
                  <span className="font-semibold">
                    {(property.title_work_files?.length || 0) +
                      (property.po_files?.length || 0)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500">Messages</span>
                  <span className="font-semibold">{messages.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Property Under Contract</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Select Contract Holder</Label>
              <Select
                value={contractClientId}
                onValueChange={setContractClientId}
              >
                <SelectTrigger className="input-field">
                  <SelectValue placeholder="Choose a client..." />
                </SelectTrigger>
                <SelectContent>
                  {assignedClients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleContractUpdate}
              className="btn-primary w-full"
              disabled={!contractClientId}
              data-testid="confirm-contract-button"
            >
              Confirm Contract
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVisibilityModal} onOpenChange={setShowVisibilityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit Visibility for {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="phone-visible"
                defaultChecked={property.phone_visible_to?.includes(
                  selectedClient?.id
                )}
                onCheckedChange={(checked) =>
                  handleVisibilityUpdate(
                    checked,
                    property.title_work_visible_to?.includes(selectedClient?.id)
                  )
                }
              />
              <Label htmlFor="phone-visible">Show phone number</Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="title-visible"
                defaultChecked={property.title_work_visible_to?.includes(
                  selectedClient?.id
                )}
                onCheckedChange={(checked) =>
                  handleVisibilityUpdate(
                    property.phone_visible_to?.includes(selectedClient?.id),
                    checked
                  )
                }
              />
              <Label htmlFor="title-visible">Show title work documents</Label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make an Offer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500">Asking Price:</p>
              <p className="text-2xl font-bold text-brand-primary">
                ${property?.price?.toLocaleString()}
              </p>
            </div>

            <div>
              <Label>Your Offer Amount ($)</Label>
              <Input
                type="number"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                className="input-field"
                placeholder="Enter your offer"
                data-testid="offer-amount-input"
              />
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <textarea
                value={offerNotes}
                onChange={(e) => setOfferNotes(e.target.value)}
                className="input-field min-h-[80px]"
                placeholder="Add any notes or conditions..."
              />
            </div>

            <Button
              onClick={submitOffer}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="submit-offer-button"
            >
              <HandCoins className="w-4 h-4 mr-2" /> Submit Offer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-5xl p-0 bg-black">
          <DialogHeader className="sr-only">
            <DialogTitle>Expanded property image</DialogTitle>
            <DialogDescription>
              Large preview of the selected property image.
            </DialogDescription>
          </DialogHeader>

          {selectedImage && (
            <img
              src={`${API}/files/${selectedImage.storage_path}?auth=${localStorage.getItem("token")}`}
              alt={property.address}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetail;
